"use client";

import React, { useEffect, useMemo, useRef } from "react";
import { BaseIndicatorProps } from "../../lib/interface/Indicator.interface";
import { IPriceScaleApi, ISeriesApi, LineSeries, PriceScaleMode, Time } from "lightweight-charts";
import { WSFearGreedPayload } from "@lacon/ws";

export interface FearGreedIndicatorProps extends BaseIndicatorProps {
  fearGreed: WSFearGreedPayload | null;
  formatTime: (timestamp: number) => number;
}

const getColorForValue = (value?: number) => {
  if (value === undefined || value === null) return "#6b7280";
  if (value >= 80) return "#16a34a"; // green
  if (value >= 60) return "#86efac"; // light green
  if (value >= 40) return "#facc15"; // yellow
  if (value >= 20) return "#fb923c"; // orange
  return "#ef4444"; // red
};

const withAlpha = (hex: string, alpha: number) => {
  const normalized = hex.trim();
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(normalized);
  if (!match) return normalized;

  const value = match[1];
  const full =
    value.length === 3
      ? value
          .split("")
          .map((c) => c + c)
          .join("")
      : value;

  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const PRICE_SCALE_ID = "fearGreedScale";
const ensureScaleOptions = (scale?: IPriceScaleApi | null) => {
  scale?.applyOptions({
    mode: PriceScaleMode.Normal,
    scaleMargins: {
      top: 0.75,
      bottom: 0,
    },
  });
};

export const FearGreedIndicator: React.FC<FearGreedIndicatorProps> = ({
  chartInstance,
  candleSeries: _candleSeries,
  isEnabled,
  fearGreed,
  formatTime,
}) => {
  const lineRef = useRef<ISeriesApi<"Line"> | null>(null);

  // Manage line series lifecycle
  useEffect(() => {
    if (!chartInstance) return;

    if (!isEnabled) {
      if (lineRef.current) {
        try {
          chartInstance.removeSeries(lineRef.current);
        } catch {}
        lineRef.current = null;
      }
      return;
    }

    if (!lineRef.current) {
      try {
        lineRef.current = chartInstance.addSeries(LineSeries, {
          color: withAlpha(getColorForValue(fearGreed?.latest?.value), 0.5),
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: false,
          priceScaleId: PRICE_SCALE_ID,
        });

        ensureScaleOptions(chartInstance.priceScale(PRICE_SCALE_ID));
        lineRef.current?.applyOptions({
          priceFormat: { type: "price", precision: 0, minMove: 1 },
        });
      } catch {}
    }

    return () => {
      if (lineRef.current) {
        try {
          chartInstance.removeSeries(lineRef.current);
        } catch {}
        lineRef.current = null;
      }
    };
  }, [chartInstance, isEnabled, fearGreed]);

  // Update series color based on latest reading
  useEffect(() => {
    if (!lineRef.current) return;
    try {
      lineRef.current.applyOptions({
        color: withAlpha(getColorForValue(fearGreed?.latest?.value), 0.5),
      });
    } catch {}
  }, [fearGreed]);

  // Update plotted data when payload changes
  useEffect(() => {
    if (!isEnabled || !lineRef.current) {
      try {
        lineRef.current?.setData([]);
      } catch {}
      return;
    }
    if (!fearGreed) {
      try {
        lineRef.current.setData([]);
      } catch {}
      return;
    }

    if (chartInstance) {
      ensureScaleOptions(chartInstance.priceScale(PRICE_SCALE_ID));
    }

    const points = (fearGreed.points || [])
      .map((point) => ({
        time: formatTime(point.timestamp) as Time,
        value: point.value,
        color: withAlpha(getColorForValue(point.value), 0.5),
      }))
      .sort((a, b) => (a.time as number) - (b.time as number));

    try {
      lineRef.current.setData(points);
    } catch {}
  }, [fearGreed, isEnabled, formatTime, chartInstance]);

  const latestInfo = useMemo(() => {
    if (!fearGreed?.latest) return null;
    const value = Math.round(fearGreed.latest.value);
    const classification = fearGreed.latest.classification || "Neutral";
    const dateLabel = new Date(fearGreed.latest.timestamp).toLocaleDateString();
    return { value, classification, dateLabel };
  }, [fearGreed]);

  if (!isEnabled) return null;

  return (
    <>
      {latestInfo && (
        <div className="absolute top-3 right-3 z-30 select-none">
          <div
            className="bg-slate-900/80 text-white px-3 py-1 rounded-md shadow-md border border-white/10"
            title={`Updated ${latestInfo.dateLabel}`}
          >
            <div className="flex items-center gap-2 text-sm font-semibold">
              <span className="text-xs uppercase tracking-wide opacity-70">
                Fear &amp; Greed
              </span>
              <span>{latestInfo.value}</span>
              <span className="text-xs opacity-70">
                {latestInfo.classification}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
